/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

@file:Suppress("DuplicatedCode")

package io.peekandpoke.klang.script.builder

import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.KlangScriptEngine
import io.peekandpoke.klang.script.KlangScriptLibrary
import io.peekandpoke.klang.script.ast.CallInfo
import io.peekandpoke.klang.script.getUniqueClassName
import io.peekandpoke.klang.script.runtime.NativeExtensionMethod
import io.peekandpoke.klang.script.runtime.NativeExtensionProperty
import io.peekandpoke.klang.script.runtime.NativeTypeInfo
import io.peekandpoke.klang.script.runtime.ParamSpec
import io.peekandpoke.klang.script.runtime.RuntimeValue
import io.peekandpoke.klang.script.runtime.callInfoOf
import io.peekandpoke.klang.script.runtime.checkArgsSize
import io.peekandpoke.klang.script.runtime.checkNoArgs
import io.peekandpoke.klang.script.runtime.convertArgToKotlin
import io.peekandpoke.klang.script.runtime.wrapAsRuntimeValue
import kotlin.jvm.JvmName
import kotlin.reflect.KClass

/**
 * One registered native top-level function.
 *
 * @property name Script-visible name.
 * @property function The legacy positional invocation closure.
 * @property paramSpecs Optional declared parameters. Set by the new
 *   `createFunction` builder; null for legacy `registerFunction*` registrations.
 *   Drives named-argument resolution at the interpreter layer.
 */
data class NativeFunctionEntry(
    val name: String,
    val function: (List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    val paramSpecs: List<ParamSpec>? = null,
)

/**
 * Defines a klang script extension
 */
class KlangScriptExtension(
    /** Registered libraries */
    val libraries: Map<String, KlangScriptLibrary>,
    /** Registered native functions */
    val functions: List<NativeFunctionEntry>,
    /** Registered native types */
    val types: Map<KClass<*>, NativeTypeInfo>,
    /** Registered native objects */
    val objects: Map<String, Any>,
    /** Registered native object extension methods */
    val extensionMethods: Map<KClass<*>, MutableMap<String, NativeExtensionMethod>>,
    /** Registered native object extension properties */
    val extensionProperties: Map<KClass<*>, MutableMap<String, NativeExtensionProperty>>,
)

/**
 * Native extension builder.
 *
 * This is used to register functions, types, and extension methods for kotlin interop.
 */
interface KlangScriptExtensionBuilder {
    companion object {
        /** Create a new [KlangScriptExtensionBuilder] */
        operator fun invoke(): KlangScriptExtensionBuilder = RegistryBuilderImpl()
    }

    /** Build the extension */
    fun buildNativeRegistry(): KlangScriptExtension

    /** Register a library with the engine */
    fun registerLibrary(library: KlangScriptLibrary)

    /** Registers a native function as a top-level function with source location */
    fun registerFunctionRaw(
        name: String,
        fn: (List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    )

    /** Register a native Kotlin type */
    fun <T : Any> registerType(cls: KClass<T>, block: NativeObjectExtensionsBuilder<T>.() -> Unit = {})

    /** Register a native Kotlin object */
    fun <T : Any> registerObject(name: String, obj: T, block: NativeObjectExtensionsBuilder<T>.() -> Unit = {})

    /** Register a native Kotlin extension method */
    fun <T : Any> registerExtensionMethod(
        receiver: KClass<T>,
        name: String,
        fn: (T, List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    )

    /** Register a native Kotlin extension method with engine access at call time. */
    fun <T : Any> registerExtensionMethodWithEngine(
        receiver: KClass<T>,
        name: String,
        fn: (T, List<RuntimeValue>, SourceLocation?, KlangScriptEngine) -> RuntimeValue,
    )

    /**
     * Internal — register a native function together with its declared
     * parameter specs (Phase 4 [createFunction] builder). Drives named-arg
     * resolution at the interpreter layer.
     */
    fun registerFunctionWithSpecs(
        name: String,
        paramSpecs: List<ParamSpec>,
        fn: (List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    )

    /**
     * Internal — register a native extension method together with its declared
     * parameter specs. Receiver is supplied separately by the binding logic
     * and is NOT included in [paramSpecs].
     */
    fun registerExtensionMethodWithSpecs(
        receiver: KClass<*>,
        name: String,
        paramSpecs: List<ParamSpec>,
        fn: (Any, List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    )

    /**
     * Register a native Kotlin extension property.
     *
     * Property access (`receiver.name` without parens) on a [ NativeObjectValue ]
     * of type [receiver] resolves to the value produced by [getter] (already
     * wrapped as a [RuntimeValue]).
     */
    fun <T : Any> registerExtensionProperty(
        receiver: KClass<T>,
        name: String,
        getter: (T) -> RuntimeValue,
    )
}

/**
 * Native extension builder implementation.
 */
class RegistryBuilderImpl : KlangScriptExtensionBuilder {
    /** Registered libraries */
    private val libraries = mutableMapOf<String, KlangScriptLibrary>()

    /** Registered native functions */
    private val nativeFunctions = mutableListOf<NativeFunctionEntry>()

    /** Registered native types */
    private val nativeTypes = mutableMapOf<KClass<*>, NativeTypeInfo>()

    /** Registered native objects */
    private val nativeObjects = mutableMapOf<String, Any>()

    /** Registered native object extension methods */
    private val nativeExtensionMethods = mutableMapOf<KClass<*>, MutableMap<String, NativeExtensionMethod>>()

    /** Registered native object extension properties */
    private val nativeExtensionProperties = mutableMapOf<KClass<*>, MutableMap<String, NativeExtensionProperty>>()

    /** Build the extension */
    override fun buildNativeRegistry(): KlangScriptExtension = KlangScriptExtension(
        libraries = libraries,
        types = nativeTypes,
        objects = nativeObjects,
        extensionMethods = nativeExtensionMethods,
        extensionProperties = nativeExtensionProperties,
        functions = nativeFunctions,
    )

    /** Register a library with the engine */
    override fun registerLibrary(library: KlangScriptLibrary) {
        libraries[library.name] = library
    }

    /** Register a native function */
    override fun registerFunctionRaw(
        name: String,
        fn: (List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    ) {
        nativeFunctions.add(NativeFunctionEntry(name, fn))
    }

    /** Register a native function with declared parameter specs (Phase 4 builder uses this). */
    override fun registerFunctionWithSpecs(
        name: String,
        paramSpecs: List<ParamSpec>,
        fn: (List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    ) {
        nativeFunctions.add(NativeFunctionEntry(name, fn, paramSpecs))
    }

    /** Register a native Kotlin type */
    override fun <T : Any> registerType(cls: KClass<T>, block: NativeObjectExtensionsBuilder<T>.() -> Unit) {
        // println("Registering native type: ${cls.simpleName} -> ${cls.getUniqueClassName()}")

        if (!nativeTypes.containsKey(cls)) {
            val qualifiedName = cls.getUniqueClassName()
            nativeTypes[cls] = NativeTypeInfo(cls, qualifiedName)
        }

        block(NativeObjectExtensionsBuilder(this, cls))
    }

    /** Register a native Kotlin object */
    override fun <T : Any> registerObject(
        name: String,
        obj: T,
        block: NativeObjectExtensionsBuilder<T>.() -> Unit,
    ) {
        nativeObjects[name] = obj

        @Suppress("UNCHECKED_CAST")
        registerType(cls = obj::class as KClass<T>, block = block)
    }

    /** Register a native Kotlin extension method */
    override fun <T : Any> registerExtensionMethod(
        receiver: KClass<T>,
        name: String,
        fn: (T, List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    ) {
        val extensionMethod = NativeExtensionMethod(
            methodName = name,
            receiverClass = receiver,
            invoker = { receiver, args, location, _ ->
                @Suppress("UNCHECKED_CAST")
                val result = fn(receiver as T, args, location)
                wrapAsRuntimeValue(result)
            })

        registerType(receiver)

        nativeExtensionMethods.getOrPut(receiver) { mutableMapOf() }[name] = extensionMethod
    }

    /** Register a native Kotlin extension method with engine access at call time. */
    override fun <T : Any> registerExtensionMethodWithEngine(
        receiver: KClass<T>,
        name: String,
        fn: (T, List<RuntimeValue>, SourceLocation?, KlangScriptEngine) -> RuntimeValue,
    ) {
        val extensionMethod = NativeExtensionMethod(
            methodName = name,
            receiverClass = receiver,
            invoker = { receiver, args, location, engine ->
                @Suppress("UNCHECKED_CAST")
                val result = fn(receiver as T, args, location, engine)
                wrapAsRuntimeValue(result)
            })

        registerType(receiver)

        nativeExtensionMethods.getOrPut(receiver) { mutableMapOf() }[name] = extensionMethod
    }

    /**
     * Used by the Phase 4 [createFunction] builder.
     * Registers an extension method together with its declared param specs so
     * the interpreter can resolve named-arg calls.
     */
    override fun registerExtensionMethodWithSpecs(
        receiver: KClass<*>,
        name: String,
        paramSpecs: List<ParamSpec>,
        fn: (Any, List<RuntimeValue>, SourceLocation?) -> RuntimeValue,
    ) {
        val extensionMethod = NativeExtensionMethod(
            methodName = name,
            receiverClass = receiver,
            invoker = { rcv, args, location, _ -> fn(rcv, args, location) },
            paramSpecs = paramSpecs,
        )
        registerType(receiver) {}
        nativeExtensionMethods.getOrPut(receiver) { mutableMapOf() }[name] = extensionMethod
    }

    override fun <T : Any> registerExtensionProperty(
        receiver: KClass<T>,
        name: String,
        getter: (T) -> RuntimeValue,
    ) {
        val extensionProperty = NativeExtensionProperty(
            propertyName = name,
            receiverClass = receiver,
            getter = { rcv ->
                @Suppress("UNCHECKED_CAST")
                getter(rcv as T)
            },
        )
        registerType(receiver) {}
        nativeExtensionProperties.getOrPut(receiver) { mutableMapOf() }[name] = extensionProperty
    }
}

/**
 * Builder for registering extension methods on a native object or type
 */
class NativeObjectExtensionsBuilder<T : Any>(
    /** The enclosing registry. Public because KSP-generated registrations in OTHER modules (`:klangscript-libs`, `:sprudel`) call it directly. */
    val builder: KlangScriptExtensionBuilder,
    /** The receiver class every method registered through this builder is attached to. Public for the same reason as [builder]. */
    val cls: KClass<T>,
) {
    /**
     * Register a no-parens property accessor on the receiver type.
     *
     * The [getter] receives the receiver instance and returns the property value;
     * it is wrapped as a [RuntimeValue] via [wrapAsRuntimeValue].
     */
    inline fun <reified R> registerProperty(
        name: String,
        noinline getter: T.() -> R,
    ) {
        builder.registerExtensionProperty(cls, name) { receiver ->
            wrapAsRuntimeValue(receiver.getter())
        }
    }

    /** Register a native extension function with variable number of parameters. Null args are passed through. */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P : Any, reified R : Any> registerVarargMethod(
        name: String, noinline fn: T.(List<P>) -> R,
    ) {
        builder.registerExtensionMethod(cls, name) { receiver, args, loc ->
            val params = List(args.size) { index ->
                convertArgToKotlin(fn = name, args = args, index = index, cls = P::class, nullable = true, loc = loc)
            } as List<P>

            val result = receiver.fn(params)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with CallInfo for location tracking. */
    // Invoked from KSP-generated registration code (klangscript-ksp RegistrationModel).
    @Suppress("UNCHECKED_CAST", "unused")
    inline fun <reified P : Any, reified R> registerVarargMethodWithCallInfo(
        name: String, noinline fn: T.(List<P>, CallInfo) -> R,
    ) {
        builder.registerExtensionMethod(cls, name) { receiver, args, loc ->
            val callInfo = callInfoOf(receiver, args, loc)

            val params = List(args.size) { index ->
                convertArgToKotlin(fn = name, args = args, index = index, cls = P::class, nullable = true, loc = loc)
            } as List<P>

            val result = receiver.fn(params, callInfo)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with no parameters */
    @JvmName("registerNativeExtensionMethod0")
    inline fun <reified R : Any> registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(Any?) -> R,
    ) {
        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkNoArgs(fn = name, args = args, location = loc)

            @Suppress("UNCHECKED_CAST")
            val result = (receiver as T).fn(null)
            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with one parameter */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P1 : Any, reified R : Any> registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(P1) -> R,
    ) {
        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkArgsSize(fn = name, args = args, expected = 1, location = loc)

            val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1

            val result = (receiver as T).fn(p1)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with two parameters */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P1 : Any, reified P2 : Any, reified R : Any> registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(P1, P2) -> R,
    ) {
        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkArgsSize(fn = name, args = args, expected = 2, location = loc)

            val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
            val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2

            val result = (receiver as T).fn(p1, p2)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with three parameters */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified R : Any> registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(P1, P2, P3) -> R,
    ) {
        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkArgsSize(fn = name, args = args, expected = 3, location = loc)

            val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
            val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
            val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3

            val result = (receiver as T).fn(p1, p2, p3)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with four parameters */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified P4 : Any, reified R : Any>
            registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(P1, P2, P3, P4) -> R,
    ) {

        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkArgsSize(fn = name, args = args, expected = 4, location = loc)

            val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
            val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
            val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3
            val p4 = convertArgToKotlin(fn = name, args = args, index = 3, cls = P4::class, nullable = null is P4, loc = loc) as P4

            val result = (receiver as T).fn(p1, p2, p3, p4)

            wrapAsRuntimeValue(result)
        }
    }

    /** Register a native extension method with five parameters */
    @Suppress("UNCHECKED_CAST")
    inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified P4 : Any, reified P5 : Any, reified R : Any>
            registerMethod(
        name: String,
        paramSpecs: List<ParamSpec> = emptyList(),
        noinline fn: T.(P1, P2, P3, P4, P5) -> R,
    ) {

        builder.registerExtensionMethodWithSpecs(cls, name, paramSpecs) { receiver, args, loc ->
            checkArgsSize(fn = name, args = args, expected = 5, location = loc)

            val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
            val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
            val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3
            val p4 = convertArgToKotlin(fn = name, args = args, index = 3, cls = P4::class, nullable = null is P4, loc = loc) as P4
            val p5 = convertArgToKotlin(fn = name, args = args, index = 4, cls = P5::class, nullable = null is P5, loc = loc) as P5

            val result = (receiver as T).fn(p1, p2, p3, p4, p5)

            wrapAsRuntimeValue(result)
        }
    }
}

/** Register a library from source code (backward compatibility) */
fun KlangScriptExtensionBuilder.registerLibrary(name: String, sourceCode: String) {
    registerLibrary(
        KlangScriptLibrary.builder(name).source(sourceCode).build()
    )
}

/** Register a native Kotlin type */
inline fun <reified T : Any> KlangScriptExtensionBuilder.registerType(
    noinline block: NativeObjectExtensionsBuilder<T>.() -> Unit = {},
) {
    registerType(cls = T::class, block = block)
}

/** Registers a native function as a top-level vararg function. Null args are passed through. */
// Also invoked from KSP-generated registration code (klangscript-ksp RegistrationModel).
@Suppress("UNCHECKED_CAST", "unused")
inline fun <reified P : Any, reified R : Any> KlangScriptExtensionBuilder.registerVarargFunction(
    name: String, noinline fn: (List<P>) -> R,
) {
    registerFunctionRaw(name) { args, loc ->
        val params = List(args.size) { index ->
            convertArgToKotlin(fn = name, args = args, index = index, cls = P::class, nullable = true, loc = loc)
        } as List<P>
        val result = fn(params)
        wrapAsRuntimeValue(result)
    }
}

/** Registers a native vararg function with inline documentation. */
// Also invoked from KSP-generated registration code (klangscript-ksp RegistrationModel).
@Suppress("UNCHECKED_CAST", "unused")
inline fun <reified P : Any, reified R : Any> KlangScriptLibrary.Builder.registerVarargFunction(
    name: String,
    docs: io.peekandpoke.klang.script.types.KlangCallable,
    noinline fn: (List<P>) -> R,
) {
    (this as KlangScriptExtensionBuilder).registerVarargFunction<P, R>(name, fn)
    registerDocs(docs)
}

/** Registers a native function as a top-level function with no parameters */
@JvmName("registerNativeFunction0")
inline fun <reified R : Any> KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (Any?) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { _, _ ->
        val result = fn(null)
        wrapAsRuntimeValue(result)
    }
}

/** Registers a native function as a top-level function with one parameter */
@Suppress("UNCHECKED_CAST")
inline fun <reified P1 : Any, reified R : Any> KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (P1) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { args, loc ->
        checkArgsSize(fn = name, args = args, expected = 1, location = loc)

        val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1

        val result = fn(p1)

        wrapAsRuntimeValue(result)
    }
}

/** Registers a native function as a top-level function with two parameters */
@Suppress("UNCHECKED_CAST")
inline fun <reified P1 : Any, reified P2 : Any, reified R : Any> KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (P1, P2) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { args, loc ->
        checkArgsSize(fn = name, args = args, expected = 2, location = loc)

        val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
        val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2

        val result = fn(p1, p2)

        wrapAsRuntimeValue(result)
    }
}

/** Registers a native function as a top-level function with three parameters */
@Suppress("UNCHECKED_CAST")
inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified R : Any>
        KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (P1, P2, P3) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { args, loc ->
        checkArgsSize(fn = name, args = args, expected = 3, location = loc)

        val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
        val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
        val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3

        val result = fn(p1, p2, p3)

        wrapAsRuntimeValue(result)
    }
}

/** Registers a native function as a top-level function with four parameters */
@Suppress("UNCHECKED_CAST")
inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified P4 : Any, reified R : Any>
        KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (P1, P2, P3, P4) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { args, loc ->
        checkArgsSize(fn = name, args = args, expected = 4, location = loc)

        val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
        val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
        val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3
        val p4 = convertArgToKotlin(fn = name, args = args, index = 3, cls = P4::class, nullable = null is P4, loc = loc) as P4

        val result = fn(p1, p2, p3, p4)

        wrapAsRuntimeValue(result)
    }
}

/** Registers a native function as a top-level function with five parameters */
@Suppress("UNCHECKED_CAST")
inline fun <reified P1 : Any, reified P2 : Any, reified P3 : Any, reified P4 : Any, reified P5 : Any, reified R : Any>
        KlangScriptExtensionBuilder.registerFunction(
    name: String,
    paramSpecs: List<ParamSpec> = emptyList(),
    noinline fn: (P1, P2, P3, P4, P5) -> R,
) {
    registerFunctionWithSpecs(name, paramSpecs) { args, loc ->
        checkArgsSize(fn = name, args = args, expected = 5, location = loc)

        val p1 = convertArgToKotlin(fn = name, args = args, index = 0, cls = P1::class, nullable = null is P1, loc = loc) as P1
        val p2 = convertArgToKotlin(fn = name, args = args, index = 1, cls = P2::class, nullable = null is P2, loc = loc) as P2
        val p3 = convertArgToKotlin(fn = name, args = args, index = 2, cls = P3::class, nullable = null is P3, loc = loc) as P3
        val p4 = convertArgToKotlin(fn = name, args = args, index = 3, cls = P4::class, nullable = null is P4, loc = loc) as P4
        val p5 = convertArgToKotlin(fn = name, args = args, index = 4, cls = P5::class, nullable = null is P5, loc = loc) as P5

        val result = fn(p1, p2, p3, p4, p5)

        wrapAsRuntimeValue(result)
    }
}

// ===== CallInfo-aware registration methods =====

/**
 * Registers a native function with CallInfo for location tracking
 *
 * Extracts source locations from RuntimeValue parameters and provides them via CallInfo.
 */
// Invoked from KSP-generated registration code (klangscript-ksp RegistrationModel).
@Suppress("UNCHECKED_CAST", "unused")
inline fun <reified P : Any, reified R> KlangScriptExtensionBuilder.registerVarargFunctionWithCallInfo(
    name: String, noinline fn: (List<P>, CallInfo) -> R,
) {
    registerFunctionRaw(name) { args, loc ->
        val callInfo = callInfoOf(null, args, loc)
        val params = List(args.size) { index ->
            convertArgToKotlin(fn = name, args = args, index = index, cls = P::class, nullable = true, loc = loc)
        } as List<P>
        val result = fn(params, callInfo)
        wrapAsRuntimeValue(result)
    }
}
